import type { PersonationContextType, LinkTarget, RegexReplace } from "@/lib/personation-context"
import {
  DEFAULT_CHAT_INTRO_MESSAGE,
  DEFAULT_CHAT_INTRO_META,
  DEFAULT_CHAT_PROMPTS,
  DEFAULT_CHAT_PROMPTS_TITLE,
  DEFAULT_CHAT_LAUNCHER_CALL_TO_ACTION,
  DEFAULT_LAUNCHER_AVATAR_SIZE,
  DEFAULT_SEARCH_PLACEHOLDER,
  DEFAULT_RECENTLY_VISITED_TITLE,
  resolveLabels,
} from "@/lib/personation-context"

export type PersonationProps = {
  fiinderApiUrl: string
  companyToken: string
  agentName?: string
  agentRole?: string
  agentAvatarUrl?: string
  companyMainColor?: string
  introMessage?: string
  introMessageMeta?: string
  promptSuggestionsTitle?: string
  promptSuggestions?: string[]
  launcherCallToAction?: string
  launcherAvatarSize?: number
  linkTarget?: LinkTarget
  forceTopLayer?: boolean
  searchPlaceholder?: string
  recentlyVisitedTitle?: string
  llmModel?: string
}

const sanitizePrompts = (list?: string[]): string[] => {
  if (!Array.isArray(list)) return []
  return list.map((item) => item.trim()).filter(Boolean)
}

const withDefault = (value: string | undefined, fallback: string) => {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : fallback
}

const resolveStringFromWindow = (key: string, fallback: string): string => {
  if (typeof window === "undefined") return fallback
  const value = (window as any)[key]
  if (typeof value === "string" && value.trim()) return value
  return fallback
}

const resolveLinkTargetFromWindow = (key: string, fallback: LinkTarget): LinkTarget => {
  if (typeof window === "undefined") return fallback
  const value = (window as any)[key]
  if (value === "_blank" || value === "_self") return value
  return fallback
}

const resolveBooleanFromWindow = (key: string, fallback: boolean): boolean => {
  if (typeof window === "undefined") return fallback
  const value = (window as any)[key]
  if (typeof value === "boolean") return value
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "1", "yes", "on"].includes(normalized)) return true
    if (["false", "0", "no", "off"].includes(normalized)) return false
  }
  return fallback
}

const resolveLinkRegexReplacesFromWindow = (): RegexReplace[] => {
  if (typeof window === "undefined") return []
  const value = (window as any).FIINDER_LINK_REGEX_REPLACES
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is RegexReplace =>
        typeof item === "object" &&
        item !== null &&
        typeof item.pattern === "string" &&
        typeof item.replacement === "string"
    )
  }
  return []
}

export function toPersonationValue(
  props: PersonationProps
): PersonationContextType {
  const sanitizedPrompts = sanitizePrompts(props.promptSuggestions)
  const finalPrompts =
    sanitizedPrompts.length > 0 ? sanitizedPrompts : DEFAULT_CHAT_PROMPTS
  const finalAvatarSize =
    props.launcherAvatarSize && props.launcherAvatarSize > 0
      ? props.launcherAvatarSize
      : DEFAULT_LAUNCHER_AVATAR_SIZE

  const linkTarget: LinkTarget =
    props.linkTarget === "_blank" || props.linkTarget === "_self"
      ? props.linkTarget
      : resolveLinkTargetFromWindow("FIINDER_LINK_TARGET", "_self")

  const searchPlaceholder = withDefault(
    props.searchPlaceholder,
    resolveStringFromWindow("FIINDER_SEARCH_PLACEHOLDER", DEFAULT_SEARCH_PLACEHOLDER)
  )

  const recentlyVisitedTitle = withDefault(
    props.recentlyVisitedTitle,
    resolveStringFromWindow("FIINDER_RECENTLY_VISITED_TITLE", DEFAULT_RECENTLY_VISITED_TITLE)
  )

  const linkRegexReplaces = resolveLinkRegexReplacesFromWindow()
  const forceTopLayer =
    typeof props.forceTopLayer === "boolean"
      ? props.forceTopLayer
      : resolveBooleanFromWindow("FIINDER_FORCE_TOP_LAYER", true)

  return {
    apiUrl: props.fiinderApiUrl,
    companyToken: props.companyToken,
    botName: props.agentName?.trim() || "Bot",
    botRole: props.agentRole?.trim() || "Assistant",
    botAvatarUrl: props.agentAvatarUrl ?? "",
    companyMainColor: props.companyMainColor?.trim() || "#e6001e",
    introMessage: withDefault(props.introMessage, DEFAULT_CHAT_INTRO_MESSAGE),
    introMessageMeta: withDefault(
      props.introMessageMeta,
      DEFAULT_CHAT_INTRO_META
    ),
    promptSuggestionsTitle: withDefault(
      props.promptSuggestionsTitle,
      DEFAULT_CHAT_PROMPTS_TITLE
    ),
    promptSuggestions: finalPrompts,
    launcherCallToAction: withDefault(
      props.launcherCallToAction,
      DEFAULT_CHAT_LAUNCHER_CALL_TO_ACTION
    ),
    launcherAvatarSize: finalAvatarSize,
    linkTarget,
    forceTopLayer,
    linkRegexReplaces,
    searchPlaceholder,
    recentlyVisitedTitle,
    labels: resolveLabels(),
  }
}

export function assignGlobals(props: PersonationProps) {
  if (typeof window === "undefined") return
  window.FIINDER_API_URL = props.fiinderApiUrl
  window.FIINDER_COMPANY_TOKEN = props.companyToken
  window.FIINDER_AGENT_NAME = props.agentName ?? "Bot"
  window.FIINDER_AGENT_ROLE = props.agentRole ?? "Assistant"
  window.FIINDER_AGENT_AVATAR_URL = props.agentAvatarUrl ?? ""
  window.FIINDER_COMPANY_MAIN_COLOR =
    props.companyMainColor?.trim() || "#e6001e"
  const intro = withDefault(props.introMessage, DEFAULT_CHAT_INTRO_MESSAGE)
  const introMeta = withDefault(
    props.introMessageMeta,
    DEFAULT_CHAT_INTRO_META
  )
  const promptsTitle = withDefault(
    props.promptSuggestionsTitle,
    DEFAULT_CHAT_PROMPTS_TITLE
  )
  const sanitizedPrompts = sanitizePrompts(props.promptSuggestions)
  const finalPrompts =
    sanitizedPrompts.length > 0 ? sanitizedPrompts : DEFAULT_CHAT_PROMPTS
  const callToAction = withDefault(
    props.launcherCallToAction,
    DEFAULT_CHAT_LAUNCHER_CALL_TO_ACTION
  )
  const avatarSize =
    props.launcherAvatarSize && props.launcherAvatarSize > 0
      ? props.launcherAvatarSize
      : DEFAULT_LAUNCHER_AVATAR_SIZE

  window.FIINDER_CHAT_INTRO_MESSAGE = intro
  window.FIINDER_CHAT_INTRO_META = introMeta
  window.FIINDER_CHAT_START_QUICK_QUESTIONS_TITLE = promptsTitle
  window.FIINDER_CHAT_START_QUICK_QUESTIONS = finalPrompts.join("|")
  window.FIINDER_CHAT_LAUNCHER_CALL_TO_ACTION = callToAction
  window.FIINDER_AVATAR_SIZE = avatarSize.toString()
  const llmModel = props.llmModel?.trim()
  if (llmModel) {
    window.FIINDER_LLM_MODEL = llmModel
  }
  if (typeof props.forceTopLayer === "boolean") {
    ;(window as any).FIINDER_FORCE_TOP_LAYER = props.forceTopLayer
  }
  if (props.linkTarget === "_blank" || props.linkTarget === "_self") {
    window.FIINDER_LINK_TARGET = props.linkTarget
  }
}
